import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import {
  LuSearch, LuGraduationCap, LuLibrary, LuLayoutGrid, LuCheckCircle2, LuBan,
} from 'react-icons/lu';
import { errorMessage, fmtDate } from './trainingUtils';
import './training.css';

/**
 * Módulos por Empresa — administrador global (is_super_admin) decide quais
 * empresas clientes podem usar cada módulo (Treinamentos, Materiais de Apoio).
 * Uma coluna por módulo; filtros e ações em massa valem para o módulo
 * selecionado nos cards do topo.
 * Desabilitar não apaga nada (liberações, histórico, arquivos); só bloqueia o acesso.
 */

const FILTERS = [
  { id: 'all', label: 'Todas' },
  { id: 'on', label: 'Habilitadas' },
  { id: 'off', label: 'Não habilitadas' },
];

const MODULE_ICON = { training: <LuGraduationCap />, materials: <LuLibrary /> };

const DISABLE_WARNING = {
  training: 'Os colaboradores perdem o acesso aos cursos até o módulo ser habilitado de novo. Liberações, matrículas e histórico são mantidos.',
  materials: 'Os colaboradores perdem o acesso aos materiais de apoio até o módulo ser habilitado de novo. Os arquivos continuam cadastrados.',
};

function Switch({ checked, onChange, disabled, label }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
      className={`module-switch ${checked ? 'is-on' : ''}`} onClick={() => onChange(!checked)}>
      <span className="module-switch-knob" />
    </button>
  );
}

export default function CompanyModulesPage() {
  const [data, setData] = useState(null);
  const [moduleKey, setModuleKey] = useState('training');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [saving, setSaving] = useState([]); // chaves "empresa:modulo"
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/company-modules');
      setData(d);
      setError('');
    } catch (e) {
      setError(errorMessage(e, 'Erro ao carregar as empresas'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3500); };

  // Matriz seguida das suas filiais (filial órfã aparece como empresa avulsa).
  const ordered = useMemo(() => {
    if (!data) return [];
    const byParent = {};
    data.companies.forEach((c) => { if (c.parent_company_id) (byParent[c.parent_company_id] = byParent[c.parent_company_id] || []).push(c); });
    const ids = new Set(data.companies.map((c) => c.id));
    const roots = data.companies.filter((c) => !c.parent_company_id || !ids.has(c.parent_company_id));
    return roots.flatMap((r) => [{ ...r, level: 0 }, ...(byParent[r.id] || []).map((f) => ({ ...f, level: 1 }))]);
  }, [data]);

  const term = search.trim().toLowerCase();
  const visible = ordered.filter((c) => {
    const on = c.modules[moduleKey]?.enabled;
    if (filter === 'on' && !on) return false;
    if (filter === 'off' && on) return false;
    return !term || `${c.name} ${c.trade_name || ''} ${c.cnpj || ''} ${c.city || ''}`.toLowerCase().includes(term);
  });

  const labelOf = (key) => data?.modules.find((m) => m.key === key)?.label || key;
  const companyName = (c) => c.trade_name || c.name;

  const handleToggle = async (companies, key, enabled) => {
    const ids = companies.map((c) => c.id);
    const label = labelOf(key);
    if (!enabled && !window.confirm(
      `Desabilitar ${label} para ${ids.length === 1 ? `"${companyName(companies[0])}"` : `${ids.length} empresas`}?\n\n${DISABLE_WARNING[key] || ''}`,
    )) return;
    const keys = ids.map((id) => `${id}:${key}`);
    setSaving((p) => [...p, ...keys]);
    setError('');
    try {
      await api.put('/company-modules', { company_ids: ids, module_key: key, enabled });
      await load();
      flash(`${label} ${enabled ? 'habilitado' : 'desabilitado'} para ${ids.length === 1 ? companyName(companies[0]) : `${ids.length} empresas`}.`);
    } catch (e) {
      setError(errorMessage(e, 'Não foi possível salvar'));
    } finally {
      setSaving((p) => p.filter((k) => !keys.includes(k)));
    }
  };

  const handleBulk = (enabled) => {
    const targets = visible.filter((c) => !!c.modules[moduleKey]?.enabled !== enabled);
    if (!targets.length) return;
    if (enabled && !window.confirm(`Habilitar ${labelOf(moduleKey)} para ${targets.length} empresa(s) da lista?`)) return;
    handleToggle(targets, moduleKey, enabled);
  };

  if (!data) {
    return <div className="page-card"><div className="training-hint">{error || 'Carregando…'}</div></div>;
  }

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Módulos por Empresa</div>
          <div className="page-card-subtitle">Defina quais empresas clientes (mentoradas) podem usar cada módulo da DHR</div>
        </div>
      </div>

      {error && <div className="alert-custom danger">{error}</div>}
      {success && <div className="alert-custom success">{success}</div>}

      <div className="module-cards">
        {data.modules.map((m) => {
          const count = data.companies.filter((c) => c.modules[m.key]?.enabled).length;
          return (
            <button key={m.key} type="button" className={`module-card ${moduleKey === m.key ? 'is-active' : ''}`} onClick={() => setModuleKey(m.key)}>
              <span className="module-card-icon">{MODULE_ICON[m.key] || <LuLayoutGrid />}</span>
              <span className="module-card-body">
                <strong>{m.label}</strong>
                <small>{m.description}</small>
              </span>
              <span className="module-card-count"><strong>{count}</strong> de {data.companies.length}</span>
            </button>
          );
        })}
      </div>

      <div className="training-toolbar">
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
          <LuSearch style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
          <input className="form-control" style={{ paddingLeft: 32, maxWidth: 'none' }} placeholder="Buscar por nome, CNPJ ou cidade"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="training-chips" style={{ marginBottom: 0 }}>
          {FILTERS.map((f) => (
            <button key={f.id} type="button" className={`training-chip ${filter === f.id ? 'is-active' : ''}`} onClick={() => setFilter(f.id)}>
              {f.id === 'all' ? f.label : `${f.label} em ${labelOf(moduleKey)}`}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="training-btn training-btn--sm" onClick={() => handleBulk(true)} disabled={!visible.some((c) => !c.modules[moduleKey]?.enabled)}>
            <LuCheckCircle2 color="var(--success)" /> Habilitar {labelOf(moduleKey)} na lista
          </button>
          <button type="button" className="training-btn training-btn--sm" onClick={() => handleBulk(false)} disabled={!visible.some((c) => c.modules[moduleKey]?.enabled)}>
            <LuBan color="var(--danger)" /> Desabilitar na lista
          </button>
        </div>
      </div>

      {!visible.length ? (
        <div className="empty-state"><p>Nenhuma empresa encontrada</p></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="training-table">
            <thead>
              <tr>
                <th>Empresa</th><th>Cidade/UF</th><th>Situação</th>
                {data.modules.map((m) => <th key={m.key}>{m.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.id}>
                  <td style={{ paddingLeft: c.level ? 34 : 10 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>
                      {c.level ? '↳ ' : ''}{companyName(c)}
                      <span className="training-badge" style={{ marginLeft: 8, background: c.type === 'matriz' ? 'var(--info-soft)' : 'var(--surface-3)', color: c.type === 'matriz' ? 'var(--info)' : 'var(--text-2)' }}>
                        {c.type === 'matriz' ? 'Matriz' : 'Filial'}
                      </span>
                    </div>
                    <div className="training-hint" style={{ marginTop: 0 }}>{c.cnpj || 'sem CNPJ'}</div>
                  </td>
                  <td>{[c.city, c.state].filter(Boolean).join('/') || '—'}</td>
                  <td>{c.active ? 'Ativa' : <span style={{ color: 'var(--danger)' }}>Inativa</span>}</td>
                  {data.modules.map((mod) => {
                    const m = c.modules[mod.key] || {};
                    const busy = saving.includes(`${c.id}:${mod.key}`);
                    return (
                      <td key={mod.key}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Switch checked={!!m.enabled} disabled={busy} label={`${mod.label} — ${companyName(c)}`}
                            onChange={(v) => handleToggle([c], mod.key, v)} />
                          <div style={{ fontSize: 12, lineHeight: 1.3 }}>
                            <div style={{ fontWeight: 700, color: m.enabled ? 'var(--accent)' : 'var(--text-3)' }}>{busy ? 'Salvando…' : m.enabled ? 'Habilitado' : 'Desabilitado'}</div>
                            {m.updated_at && (
                              <div className="training-hint" style={{ marginTop: 0 }}>
                                {m.enabled ? `desde ${fmtDate(m.enabled_at)}` : `alterado em ${fmtDate(m.updated_at)}`}{m.updated_by ? ` · ${m.updated_by}` : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="training-hint" style={{ marginTop: 12 }}>
        Matriz e filiais são habilitadas separadamente: cada colaborador usa o módulo conforme a empresa do próprio cadastro.
      </div>
    </div>
  );
}
